import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "../api/client";
import { Notice } from "../components/ui";
import { CONFIRMATION_STATUSES, confirmationFilters, groupConfirmations } from "./confirmation-domain";

const COLUMN_COLORS = {
  "noch zu organisieren": "#b8b8b0",
  "eingefordert / noch nicht erhalten": "#E0C696",
  erhalten: "#7BA393",
};

function useData(path) {
  const [state, setState] = useState({ status: "loading", data: null });
  const reload = useCallback(() => {
    setState((current) => ({ ...current, status: "loading" }));
    api(path)
      .then((data) => setState({ status: "success", data }))
      .catch((error) => setState((current) => ({ ...current, status: "error", error })));
  }, [path]);
  useEffect(reload, [reload]);
  return { ...state, reload, setData: (update) => setState((current) => ({ ...current, data: typeof update === "function" ? update(current.data) : update })) };
}

export function ConfirmationView({ route, navigate, user }) {
  const confirmations = useData("/confirmations?limit=1000");
  const mandates = useData("/mandates?limit=1000");
  const employees = useData("/employees?limit=1000");
  const types = useData("/reference-data/bestaetigungsarten?limit=100");
  const statuses = useData("/reference-data/bestaetigungsstatus?limit=100");
  const [updating, setUpdating] = useState("");
  const [updateError, setUpdateError] = useState("");
  const [draggingId, setDraggingId] = useState("");
  const selectedCard = useRef(null);
  const canEdit = ["administrator", "manager"].includes(user?.berechtigungsstufe);
  const states = [confirmations, mandates, employees, types, statuses];
  const failure = states.find((state) => state.status === "error");
  const loading = states.some((state) => state.status === "loading");
  const mandateMap = useMemo(
    () => Object.fromEntries((mandates.data?.items || []).map((item) => [item.id, item])),
    [mandates.data],
  );
  const typeMap = useMemo(
    () => Object.fromEntries((types.data?.items || []).map((item) => [item.id, item.name])),
    [types.data],
  );
  const employeeMap = useMemo(
    () => Object.fromEntries((employees.data?.items || []).map((item) => [item.id, item.name])),
    [employees.data],
  );
  const statusByName = useMemo(
    () => Object.fromEntries((statuses.data?.items || []).map((item) => [item.name, item.id])),
    [statuses.data],
  );
  const summary = useMemo(
    () => groupConfirmations(confirmations.data?.items, mandates.data?.items, statuses.data?.items),
    [confirmations.data, mandates.data, statuses.data],
  );
  const items = summary.items;
  const filters = confirmationFilters(route?.search || window.location.search);
  const selectedId = filters.id;

  useEffect(() => {
    if (selectedId && selectedCard.current) {
      selectedCard.current.focus();
      selectedCard.current.scrollIntoView?.({ block: "nearest" });
    }
  }, [selectedId, confirmations.status, loading]);

  const changeStatus = async (item, statusName) => {
    const statusId = statusByName[statusName];
    if (!statusId || statusId === item.bestaetigungsstatusId || !canEdit) return;
    const previousItems = confirmations.data.items;
    setUpdating(item.id);
    setUpdateError("");
    confirmations.setData((data) => ({ ...data, items: data.items.map((value) => value.id === item.id ? { ...value, bestaetigungsstatusId: statusId } : value) }));
    try {
      const saved = await api(`/confirmations/${item.id}`, {
        method: "PATCH",
        body: JSON.stringify({
          bestaetigungsstatusId: statusId,
          version: item.version,
        }),
        successMessage: "Bestätigungsstatus gespeichert.",
      });
      confirmations.setData((data) => ({ ...data, items: data.items.map((value) => value.id === item.id ? { ...value, ...(saved?.id ? saved : {}), bestaetigungsstatusId: statusId } : value) }));
      setUpdating("");
    } catch (error) {
      confirmations.setData((data) => ({ ...data, items: previousItems }));
      setUpdateError(error.message);
      setUpdating("");
    }
  };

  const openConfirmation = (item) => navigate(`/bestaetigungen?mandatId=${encodeURIComponent(item.mandatId)}&bestaetigungId=${encodeURIComponent(item.id)}`);

  if (failure?.error?.status === 403) {
    return <><header className="page-header"><h1>Bestätigungen</h1></header><Notice tone="error"><strong>Kein Zugriff.</strong> Bestätigungskennungen und Projektdaten dürfen mit dieser Anmeldung nicht gelesen werden.</Notice></>;
  }

  return <>
    <header className="page-header">
      <h1>Bestätigungen</h1>
      <span>{items.length} Drittbestätigungen aus {new Set(items.map((item) => item.mandatId)).size} Projekten</span>
    </header>
    {loading && <Notice>Drittbestätigungen und Projekte werden geladen …</Notice>}
    {failure && <Notice tone="error">Daten konnten nicht geladen werden: {failure.error?.message} <button type="button" className="button secondary" onClick={() => states.filter((state) => state.status === "error").forEach((state) => state.reload())}>Erneut versuchen</button></Notice>}
    {updateError && <Notice tone="error">Bestätigungsstatus konnte nicht gespeichert werden: {updateError}</Notice>}
    {!loading && !failure && summary.rejectedCount > 0 && <Notice tone="error">Datenqualitätswarnung: {summary.rejectedCount} Drittbestätigung{summary.rejectedCount === 1 ? "" : "en"} wird{summary.rejectedCount === 1 ? "" : " werden"} nicht gezählt ({summary.orphaned.length} ohne auflösbares Projekt, {summary.invalid.length} mit ungültigem oder inaktivem Status).</Notice>}
    {!loading && !failure && items.length === 0 && <div className="card"><p className="empty-hint">Noch keine Projekte mit aktivierter Drittbestätigung. Im Modul „Projekte“ bei einem Projekt „Drittbestätigung benötigt“ ankreuzen, um es hier erscheinen zu lassen.</p></div>}
    {!loading && !failure && items.length > 0 && <div className="confirmation-board">
      {CONFIRMATION_STATUSES.map((status) => {
        const columnItems = summary.groups[status];
        return <section className={`confirmation-column${draggingId ? " is-drop-target" : ""}`} key={status} data-status={status}
          onDragOver={(event) => { if (canEdit) event.preventDefault(); }}
          onDrop={(event) => { event.preventDefault(); const item = items.find((value) => value.id === event.dataTransfer.getData("text/plain")); setDraggingId(""); if (item) changeStatus(item, status); }}>
          <header style={{ borderBottomColor: COLUMN_COLORS[status] }}>
            <h2>{status.toUpperCase()}</h2>
            <span style={{ backgroundColor: `${COLUMN_COLORS[status]}22` }}>{columnItems.length}</span>
          </header>
          {columnItems.length === 0 && <p className="empty-hint">Keine Einträge</p>}
          {columnItems.map((item) => {
            const mandate = mandateMap[item.mandatId];
            return <article className={`confirmation-card${selectedId === item.id ? " is-selected" : ""}`} key={item.id} role="button" tabIndex="0"
              ref={selectedId === item.id ? selectedCard : null} aria-current={selectedId === item.id ? "true" : undefined}
              draggable={canEdit && updating !== item.id}
              onDragStart={(event) => { event.dataTransfer.setData("text/plain", item.id); event.dataTransfer.effectAllowed = "move"; setDraggingId(item.id); }}
              onDragEnd={() => setDraggingId("")}
              onClick={() => openConfirmation(item)} onKeyDown={(event) => { if (event.key === "Enter" || event.key === " ") { event.preventDefault(); openConfirmation(item); } }}>
              <strong>{mandate.name}</strong>
              <small>{typeMap[item.bestaetigungsartId] || "Ungültiger Wert"} · {employeeMap[mandate.mandatsleitungId] || "–"} (ML)</small>
              <select aria-label={`Status für ${mandate.name} – ${typeMap[item.bestaetigungsartId]}`} value={status} disabled={!canEdit || updating === item.id} onClick={(event) => event.stopPropagation()} onChange={(event) => { event.stopPropagation(); changeStatus(item, event.target.value); }}>
                {CONFIRMATION_STATUSES.map((value) => <option key={value}>{value}</option>)}
              </select>
            </article>;
          })}
        </section>;
      })}
    </div>}
  </>;
}
